import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import TeamImprovements from './TeamImprovements';

const { FiUsers, FiLayers, FiTrash2, FiToggleLeft, FiToggleRight, FiCopy, FiCheck, FiEdit2, FiAward, FiX, FiSave, FiLogOut, FiTrendingUp, FiInfo } = FiIcons;

const TeamManagement = () => {
  const { user, isTeamOwner, updateUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('members');
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [elements, setElements] = useState([]);
  const [teamElements, setTeamElements] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [clusterRelations, setClusterRelations] = useState([]);
  const [teamInfo, setTeamInfo] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  
  const [editingMember, setEditingMember] = useState(null);

  useEffect(() => {
    if (user?.teamId) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const promises = [fetchTeamDetails(), fetchMembers()]; 
      
      if (isTeamOwner) {
        promises.push(fetchElements());
      }
      
      await Promise.all(promises);
    } catch (err) {
      console.error("Error fetching team data:", err);
      setError("Fehler beim Laden der Team-Daten.");
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamDetails = async () => {
    try {
      const res = await apiService.getTeamById(user.teamId);
      if (res && (res.status === 'success' || res.data)) {
         setTeamInfo(res.data || res);
      }
    } catch (e) {
      console.error("Error fetching team details:", e);
    }
  };

  const fetchMembers = async () => {
    try {
      const relationsRes = await apiService.getUserTeamRelations({ team_id: user.teamId });
      if (relationsRes.status === 'success' && Array.isArray(relationsRes.data)) {
         const relations = relationsRes.data;
         
         const memberPromises = relations.map(async (rel) => {
            try {
               const userRes = await apiService.getUserById(rel.user_id);
               let userData = null;
               if (userRes) {
                 if (Array.isArray(userRes.data)) {
                   userData = userRes.data[0];
                 } else if (userRes.data && typeof userRes.data === 'object') {
                   userData = userRes.data;
                 } else {
                   userData = userRes;
                 }
               }
               if (userData) {
                 const name = userData.name || `${userData.firstname || ''} ${userData.lastname || ''}`.trim();
                 const email = userData.email || userData.mail;
                 if (name || email) {
                    return {
                      ...userData,
                      id: userData.id || rel.user_id,
                      name: name || 'Kein Name',
                      email: email || 'Keine E-Mail',
                      relationId: rel.id,
                      role: rel.role || 'member',
                      feedback_active: userData.feedback_active,
                      is_team_owner: userData.is_team_owner
                    };
                 }
               }
               return null;
            } catch (e) {
               console.warn(`Could not fetch user ${rel.user_id}`, e);
               return null;
            }
         });
         
         const fetchedMembers = (await Promise.all(memberPromises)).filter(m => m !== null);
         setMembers(fetchedMembers);
      } else {
        setMembers([]);
      }
    } catch (e) {
      console.error("Error in fetchMembers", e);
      setMembers([]);
    }
  };

  const fetchElements = async () => {
    try {
      const [allElementsRes, teamUsageRes, clustersRes, relationsRes] = await Promise.all([
        apiService.getElements(),
        apiService.getTeamUsesElement({ team_id: user.teamId }),
        apiService.getClusters(),
        apiService.getClusterElementRelations()
      ]);

      if (allElementsRes.status === 'success' && Array.isArray(allElementsRes.data)) {
        setElements(allElementsRes.data);
      } else {
        setElements([]);
      }
      
      if (teamUsageRes.status === 'success' && Array.isArray(teamUsageRes.data)) {
        setTeamElements(teamUsageRes.data);
      } else {
        setTeamElements([]);
      }

      if (clustersRes.status === 'success' && Array.isArray(clustersRes.data)) {
        setClusters(clustersRes.data);
      } else {
        setClusters([]);
      }

      if (relationsRes.status === 'success' && Array.isArray(relationsRes.data)) {
        setClusterRelations(relationsRes.data);
      } else {
        setClusterRelations([]);
      }
    } catch (e) {
      console.error("Error in fetchElements", e);
    }
  };

  const handleRemoveMember = async (member) => {
    if (window.confirm(`Möchten Sie ${member.name || member.email} wirklich aus dem Team entfernen?`)) {
      try {
        await apiService.deleteUserTeamRelation(member.relationId);
        setMembers(prev => prev.filter(m => m.id !== member.id));
      } catch (err) {
        console.error("Error removing member:", err);
        alert("Fehler beim Entfernen des Mitglieds.");
      }
    }
  };

  const handleEditMember = (member) => {
    setEditingMember({ ...member });
  };

  const handleSaveMember = async (e) => {
    e.preventDefault();
    if (!editingMember) return;

    try {
       await apiService.updateUser(editingMember.id, {
         firstname: editingMember.firstname,
         lastname: editingMember.lastname
       });
       
       setMembers(prev => prev.map(m => m.id === editingMember.id ? {
          ...m, 
          firstname: editingMember.firstname, 
          lastname: editingMember.lastname,
          name: `${editingMember.firstname || ''} ${editingMember.lastname || ''}`.trim() || 'Kein Name'
       } : m));
       
       setEditingMember(null);
    } catch (err) {
       console.error("Error updating member:", err);
       alert("Fehler beim Aktualisieren des Mitglieds.");
    }
  };

  const handlePromoteMember = async (member) => {
     if (!window.confirm(`Möchten Sie ${member.name} wirklich zum Team-Owner machen? Dies gibt dem Nutzer volle Verwaltungsrechte für das Team.`)) return;
     
     try {
        await apiService.updateUser(member.id, { is_team_owner: 1 });
        setMembers(prev => prev.map(m => m.id === member.id ? { ...m, is_team_owner: 1 } : m));
        alert(`${member.name} ist nun ein Team-Owner.`);
     } catch (err) {
        console.error("Error promoting member:", err);
        alert("Fehler beim Befördern des Mitglieds.");
     }
  };

  const handleToggleElement = async (element) => {
    const activeRelation = teamElements.find(te => te.element_id == element.id);
    
    try {
      if (activeRelation) {
        // Toggle existing record
        const newStatus = activeRelation.is_used == 1 ? 0 : 1;
        await apiService.updateTeamUsesElement(activeRelation.id, {
          is_used: newStatus
        });
        setTeamElements(prev => prev.map(te => 
          te.id === activeRelation.id ? { ...te, is_used: newStatus } : te
        ));
      } else {
        // Create new record
        const res = await apiService.createTeamUsesElement({
          team_id: parseInt(user.teamId),
          element_id: parseInt(element.id),
          is_used: 1
        });
        
        if (res.status === 'success') {
            // Re-fetch to be safe or verify ID return
            // For now, we'll optimistically update if we can, but getting the ID is best.
            // Let's re-fetch team usage to ensure we have the correct ID for future toggles.
            const newUsageRes = await apiService.getTeamUsesElement({ team_id: user.teamId });
            if (newUsageRes.status === 'success' && Array.isArray(newUsageRes.data)) {
                setTeamElements(newUsageRes.data);
            }
        }
      }
    } catch (err) {
      console.error("Error toggling element:", err);
      alert("Fehler beim Ändern des Status.");
    }
  };

  const handleLeaveTeam = async () => {
    if (!window.confirm("Möchten Sie das Team wirklich verlassen? Alle Ihre Bewertungen in diesem Team werden unwiderruflich gelöscht.")) return;

    setLoading(true);

    try {
      const evalRes = await apiService.getEvaluations({ user_id: user.id });
      if (evalRes.status === 'success' && Array.isArray(evalRes.data)) {
        const deletePromises = evalRes.data.map(evalItem => 
          apiService.deleteEvaluation(evalItem.id)
        );
        await Promise.all(deletePromises);
      }

      let relationIdToRemove = null;
      const relRes = await apiService.getUserTeamRelations({ 
        user_id: user.id, 
        team_id: user.teamId 
      });
      
      if (relRes.status === 'success') {
          const relations = Array.isArray(relRes.data) ? relRes.data : [relRes.data].filter(Boolean);
          const relation = relations.find(r => r.team_id == user.teamId && r.user_id == user.id);
          if (relation) {
              relationIdToRemove = relation.id;
          }
      }

      // If we found specific relation
      if (relationIdToRemove) {
        await apiService.deleteUserTeamRelation(relationIdToRemove);
      } else {
        // Fallback: try to find any relation for this user if specific one failed
        const fallbackRes = await apiService.getUserTeamRelations({ user_id: user.id });
         if (fallbackRes.status === 'success') {
              const relations = Array.isArray(fallbackRes.data) ? fallbackRes.data : [fallbackRes.data].filter(Boolean);
              // Filter for the current team
              const relation = relations.find(r => r.team_id == user.teamId);
              if (relation) {
                   await apiService.deleteUserTeamRelation(relation.id);
              }
          }
      }
      
      // Update local context
      updateUser({ 
        ...user, 
        hasTeam: false, 
        teamId: null,
        is_team_owner: 0
      });
      navigate('/team-setup');

    } catch (e) {
      console.error("Error leaving team:", e);
      alert("Fehler beim Verlassen des Teams: " + (e.message || "Unbekannter Fehler"));
    } finally {
        setLoading(false);
    }
  };

  const copyToClipboard = () => {
     if (teamInfo?.code) {
        navigator.clipboard.writeText(teamInfo.code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
     }
  };

  const getInitials = (member) => {
    if (member.name && member.name !== 'Kein Name') return member.name.charAt(0).toUpperCase();
    if (member.email) return member.email.charAt(0).toUpperCase();
    return '?';
  };

  const getName = (member) => {
    return member.name || 'Kein Name';
  };

  // Helper to group elements by cluster
  const getClusteredElements = () => {
    const grouped = {};
    const noCluster = [];

    // Initialize groups for all clusters
    clusters.forEach(c => {
      grouped[c.id] = { ...c, elements: [] };
    });

    elements.forEach(element => {
      const relation = clusterRelations.find(r => r.element_id === element.id);
      if (relation && grouped[relation.cluster_id]) {
        grouped[relation.cluster_id].elements.push(element);
      } else {
        noCluster.push(element);
      }
    });

    const result = Object.values(grouped).filter(g => g.elements.length > 0);
    if (noCluster.length > 0) {
      result.push({
        id: 'none',
        title: 'Allgemein / Ohne Cluster',
        description: 'Elemente ohne spezifische Cluster-Zuordnung',
        elements: noCluster
      });
    }
    
    return result;
  };

  return (
    <div className="p-6 w-full relative">
      <div className="mb-6 flex-shrink-0 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
            <h1 className="text-2xl font-bold text-brand-dark flex items-center">
              <SafeIcon icon={FiUsers} className="w-7 h-7 mr-3 text-brand-secondary" />
              Team Verwaltung
            </h1>
            <p className="text-gray-600 mt-2">
              {isTeamOwner 
                ? "Verwalten Sie Teammitglieder und Module." 
                : "Informationen zu Ihrem aktuellen Team."}
            </p>
        </div>
        
        {isTeamOwner && teamInfo && teamInfo.code && (
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 flex flex-col items-start min-w-[200px]">
                  <span className="text-xs text-gray-500 uppercase tracking-wider font-semibold block mb-1">Team Name</span>
                  <span className="text-lg font-bold text-gray-900">{teamInfo.name}</span>
              </div>
              
              <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 flex flex-col items-start min-w-[200px]">
                <span className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-1">Team Code</span>
                <div className="flex items-center gap-2">
                    <code className="bg-gray-100 text-gray-800 px-3 py-1 rounded text-lg font-mono font-bold tracking-widest">
                        {teamInfo.code}
                    </code>
                    <button 
                        onClick={copyToClipboard}
                        className="p-2 text-gray-500 hover:text-brand-secondary hover:bg-brand-primary/10 rounded-full transition-colors"
                        title="Code kopieren"
                    >
                        <SafeIcon icon={copied ? FiCheck : FiCopy} className={`w-5 h-5 ${copied ? 'text-green-500' : ''}`} />
                    </button>
                </div>
                {copied && <span className="text-xs text-green-600 mt-1">Kopiert!</span>}
              </div>
            </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow min-h-[500px]">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('members')}
              className={`
                group inline-flex items-center py-4 px-6 border-b-2 font-medium text-sm focus:outline-none transition-colors
                ${activeTab === 'members'
                  ? 'border-brand-secondary text-brand-secondary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
              `}
            >
              <SafeIcon 
                icon={FiUsers} 
                className={`-ml-0.5 mr-2 h-5 w-5 ${activeTab === 'members' ? 'text-brand-secondary' : 'text-gray-400'}`}
              />
              Mitglieder
            </button>
            
            {isTeamOwner && (
              <button
                onClick={() => setActiveTab('elements')}
                className={`
                  group inline-flex items-center py-4 px-6 border-b-2 font-medium text-sm focus:outline-none transition-colors
                  ${activeTab === 'elements'
                    ? 'border-brand-secondary text-brand-secondary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                `}
              >
                <SafeIcon 
                  icon={FiLayers} 
                  className={`-ml-0.5 mr-2 h-5 w-5 ${activeTab === 'elements' ? 'text-brand-secondary' : 'text-gray-400'}`}
                />
                Elemente & Module
              </button>
            )}

            <button
              onClick={() => setActiveTab('improvements')}
              className={`
                group inline-flex items-center py-4 px-6 border-b-2 font-medium text-sm focus:outline-none transition-colors
                ${activeTab === 'improvements'
                  ? 'border-brand-secondary text-brand-secondary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
              `}
            >
              <SafeIcon 
                icon={FiTrendingUp} 
                className={`-ml-0.5 mr-2 h-5 w-5 ${activeTab === 'improvements' ? 'text-brand-secondary' : 'text-gray-400'}`}
              />
              Verbesserungen
            </button>
          </nav>
        </div>

        <div className="p-6">
          {loading && members.length === 0 ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
            </div>
          ) : error ? (
            <div className="text-red-500 p-4 bg-red-50 rounded-md">{error}</div>
          ) : (
            <>
              {activeTab === 'members' && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-medium text-brand-dark">Team Mitglieder ({members.length})</h3>
                  </div>
                  <ul className="divide-y divide-gray-200">
                    {members.map((member) => (
                      <li key={member.id} className="py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center text-brand-secondary font-bold shrink-0">
                            {getInitials(member)}
                          </div>
                          <div className="ml-4">
                            <div className="flex items-center gap-2">
                                <div className="text-sm font-medium text-gray-900">{getName(member)}</div>
                                {(member.is_team_owner === 1) && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                        Owner
                                    </span>
                                )}
                            </div>
                            <div className="text-sm text-gray-500">{member.email}</div>
                          </div>
                          {member.id === user.id && (
                              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Du
                              </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 self-end sm:self-auto">
                          {member.id !== user.id && isTeamOwner && (
                            <>
                                <button
                                  onClick={() => handleEditMember(member)}
                                  className="text-gray-600 hover:text-brand-secondary p-2 rounded-full hover:bg-brand-primary/10 transition-colors"
                                  title="Mitglied bearbeiten"
                                >
                                  <SafeIcon icon={FiEdit2} className="w-5 h-5" />
                                </button>
                                
                                {member.is_team_owner !== 1 && (
                                    <button
                                      onClick={() => handlePromoteMember(member)}
                                      className="text-gray-600 hover:text-yellow-600 p-2 rounded-full hover:bg-yellow-50 transition-colors"
                                      title="Zum Owner machen"
                                    >
                                      <SafeIcon icon={FiAward} className="w-5 h-5" />
                                    </button>
                                )}

                                <button
                                  onClick={() => handleRemoveMember(member)}
                                  className="text-red-600 hover:text-red-900 p-2 rounded-full hover:bg-red-50 transition-colors"
                                  title="Mitglied entfernen"
                                >
                                  <SafeIcon icon={FiTrash2} className="w-5 h-5" />
                                </button>
                            </>
                          )}
                        </div>
                      </li>
                    ))}
                    {members.length === 0 && <li className="py-4 text-center text-gray-500">Keine Mitglieder gefunden.</li>}
                  </ul>
                  
                  {!isTeamOwner && (
                    <div className="mt-8 border-t border-gray-100 pt-6">
                      <button
                        onClick={handleLeaveTeam}
                        className="text-red-600 hover:text-red-800 text-sm font-medium flex items-center"
                      >
                        <SafeIcon icon={FiLogOut} className="w-4 h-4 mr-2" />
                        Team verlassen
                      </button>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'elements' && isTeamOwner && (
                <div className="space-y-8">
                    <div className="mb-4">
                         <h3 className="text-lg font-medium text-brand-dark mb-2">Elemente & Module konfigurieren</h3>
                         <p className="text-sm text-gray-600">
                             Wählen Sie aus, welche Elemente und Module für Ihr Team aktiv sein sollen. 
                             Inaktive Elemente werden im Dashboard und in Umfragen ausgeblendet.
                         </p>
                    </div>
                    
                    {getClusteredElements().map((group) => (
                        <div key={group.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                            <h4 className="font-bold text-brand-secondary mb-3 flex items-center">
                                <span className="w-2 h-8 bg-brand-secondary rounded-full mr-3"></span>
                                {group.title}
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pl-5">
                                {group.elements.map(element => {
                                    const isUsed = teamElements.find(te => te.element_id == element.id)?.is_used == 1;
                                    return (
                                        <div 
                                            key={element.id} 
                                            className={`
                                                flex justify-between items-center p-3 rounded-lg border transition-all cursor-pointer
                                                ${isUsed ? 'bg-white border-brand-primary shadow-sm' : 'bg-gray-100 border-transparent opacity-70 hover:opacity-100'}
                                            `}
                                            onClick={() => handleToggleElement(element)}
                                        >
                                            <div>
                                                <span className={`font-medium ${isUsed ? 'text-gray-900' : 'text-gray-500'}`}>
                                                    {element.title}
                                                </span>
                                            </div>
                                            <div className={`
                                                w-10 h-6 flex items-center bg-gray-300 rounded-full p-1 duration-300 ease-in-out
                                                ${isUsed ? 'bg-brand-primary' : ''}
                                            `}>
                                                <div className={`
                                                    bg-white w-4 h-4 rounded-full shadow-md transform duration-300 ease-in-out
                                                    ${isUsed ? 'translate-x-4' : ''}
                                                `}></div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
              )}

              {activeTab === 'improvements' && (
                <TeamImprovements />
              )}
            </>
          )}
        </div>
      </div>
      
      {/* Edit Member Modal */}
      {editingMember && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 relative">
                 <button 
                    onClick={() => setEditingMember(null)}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                 >
                    <SafeIcon icon={FiX} className="w-5 h-5" />
                 </button>
                 
                 <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                    <SafeIcon icon={FiEdit2} className="mr-2 text-brand-secondary" />
                    Mitglied bearbeiten
                 </h3>
                 
                 <form onSubmit={handleSaveMember} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Vorname</label>
                        <input 
                            type="text"
                            value={editingMember.firstname || ''}
                            onChange={(e) => setEditingMember({...editingMember, firstname: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-brand-primary focus:border-brand-primary"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nachname</label>
                        <input 
                            type="text"
                            value={editingMember.lastname || ''}
                            onChange={(e) => setEditingMember({...editingMember, lastname: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-brand-primary focus:border-brand-primary"
                        />
                    </div>
                    <div className="pt-4 flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={() => setEditingMember(null)}
                            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                        >
                            Abbrechen
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 text-white bg-brand-primary rounded-lg hover:bg-brand-dark flex items-center"
                        >
                            <SafeIcon icon={FiSave} className="mr-2 w-4 h-4" />
                            Speichern
                        </button>
                    </div>
                 </form>
            </div>
        </div>
      )}
    </div>
  );
};

export default TeamManagement;